// Takvimlerde "bu çalışan bugün izinli mi?" sorusunu yanıtlayan ortak katman.
//
// İzinler iki koleksiyonda tutuluyor:
//   - LeaveRequests  : çalışanın kendi talebi (ProfilePage). Alanlar: leaveType,
//                      startDate, endDate, status = PENDING | APPROVED | REJECTED
//   - EmployeeLeaves : yöneticinin İzin Yönetimi > "İzin Ekle" ile girdiği kayıt.
//                      Alanlar: typeKey, startDate, workReturnDate (son izin
//                      günü), status = APPROVED (eski kayıtlarda hiç olmayabilir)
//
// Takvimde yalnızca fiilen kullanılan izinler gösterilir: onaylanmış kayıtlar.
// Bekleyen (PENDING) veya reddedilen talepler takvime düşmez.

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { translateLeaveType } from './leaveBalance';
import { toDateKey } from './turkishHolidays';

// Tek bir izin kaydının kapsayabileceği en fazla gün. Bozuk bir tarih
// (ör. 2099 bitişli kayıt) yüzünden döngünün kilitlenmesini engeller.
const MAX_LEAVE_SPAN_DAYS = 400;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

// 'yyyy-mm-dd' metni, Date veya Firestore Timestamp → yerel gece yarısı Date.
// Tarih metinleri Date yapıcısında UTC olarak yorumlandığı için gün kayması
// yaşanmasın diye ayrıştırma elle yapılıyor.
export const parseLeaveDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = value.match(DATE_ONLY);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }
  try {
    const date = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  } catch {
    return null;
  }
};

// Kayıt onaylanmış sayılır mı? EmployeeLeaves'in eski kayıtlarında status alanı
// bulunmuyor; leaveBalance.js ile aynı varsayım uygulanır: onaylanmış.
const isApproved = (record) => (record.status || 'APPROVED') === 'APPROVED';

// İki koleksiyonun farklı alan adlarını tek bir biçime indirger.
export const normalizeLeaveRecord = (record, source) => {
  const typeKey = record.typeKey || record.leaveType || null;
  const start = parseLeaveDate(record.startDate);
  // EmployeeLeaves'te bitiş tarihi workReturnDate alanında saklanır
  // (AddLeaveModal "Bitiş Tarihi" girdisini oraya yazar).
  const end = parseLeaveDate(record.workReturnDate || record.endDate) || start;
  if (!start || !end || end < start) return null;

  return {
    id: record.id,
    source,
    employeeId: record.employeeId || null,
    typeKey,
    label: typeKey ? translateLeaveType(typeKey) : 'İzin',
    start,
    end,
    leaveDays: Number(record.leaveDays) || 0,
    reason: record.reason || '',
    status: record.status || 'APPROVED',
  };
};

/**
 * Bir çalışanın onaylanmış izinlerini iki koleksiyondan çeker.
 * Sorgular tek eşitlik koşuluyla (employeeId) yapılır; status ayıklaması
 * istemci tarafında yapılır, böylece bileşik dizin gerekmez ve status alanı
 * olmayan eski kayıtlar da kapsanır.
 */
export const fetchEmployeeLeaves = async (employeeId) => {
  if (!employeeId) return [];

  const [requestSnap, leaveSnap] = await Promise.all([
    getDocs(query(collection(db, 'LeaveRequests'), where('employeeId', '==', employeeId))),
    getDocs(query(collection(db, 'EmployeeLeaves'), where('employeeId', '==', employeeId))),
  ]);

  const fromRequests = requestSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter(isApproved)
    .map((r) => normalizeLeaveRecord(r, 'LeaveRequests'));

  const fromLeaves = leaveSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter(isApproved)
    .map((r) => normalizeLeaveRecord(r, 'EmployeeLeaves'));

  return [...fromRequests, ...fromLeaves].filter(Boolean);
};

/**
 * İzin kayıtlarını "yyyy-mm-dd → o gün geçerli izinler" haritasına açar.
 * Takvim hücreleri O(1) sorgulayabilsin diye her gün ayrı ayrı işaretlenir.
 */
export const buildLeaveDayMap = (leaves = []) => {
  const map = new Map();
  leaves.forEach((leave) => {
    if (!leave || !leave.start || !leave.end) return;
    const cursor = new Date(leave.start);
    let guard = 0;
    while (cursor <= leave.end && guard < MAX_LEAVE_SPAN_DAYS) {
      const key = toDateKey(cursor);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(leave);
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
  });
  return map;
};

// Belirli bir güne düşen izinler (yoksa boş dizi).
export const getLeavesForDate = (leaveDayMap, date) => {
  if (!leaveDayMap || !date) return [];
  const day = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(day.getTime())) return [];
  return leaveDayMap.get(toDateKey(day)) || [];
};

// Takvim rozetinde/tooltip'inde kullanılan kısa metin: "Yıllık İzin, Raporlu"
export const describeLeaves = (leaves = []) => {
  const labels = [...new Set(leaves.map((l) => l.label).filter(Boolean))];
  return labels.length > 0 ? labels.join(', ') : 'İzinli';
};
